// 056A Adolescente Mujer / 056B Adolescente Hombre — un solo formulario configurado por sexo (ver `bloques056abDatos.ts` para el papel y
// las decisiones). Encabezado MSP, peso y talla (IMC calculado), estadio de Tanner y las cuatro curvas de la lámina sobre el historial del
// paciente. Al guardar deja, en UNA transacción, los `Observation` de peso y talla y el de Tanner.
import { useMedplum, useResource } from '@medplum/react-hooks';
import type { Observation, Patient } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { Bloque, CamposSimples } from './camposSimples';
import { observacionesDeToma } from './signosVitalesToma';
import { GraficasAdolescente } from './GraficasAdolescente';
import { datosCurvasAdolescentePdf } from '../../paciente/curvasAdolescentePdf';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { CAMPOS_MEDICION, CAMPOS_TANNER, estadoInicial056ab, imcDe, observacionTanner, paresDe056ab, tomaDe056ab, type Estado056ab, type SexoAdolescente } from './bloques056abDatos';

export function crearFormularioAdolescente(codigo: '056A' | '056B', sexo: SexoAdolescente): (props: FormularioDiscretoProps) => JSX.Element {
  return function FormularioAdolescente({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
    const { t } = useTranslation();
    const medplum = useMedplum();
    const paciente = useResource<Patient>(subject);
    const { guardando, error, guardar } = useGuardado();

    const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
    const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, codigo, t(`hub.formulario.${codigo}`));
    const [estado, setEstado] = useState<Estado056ab>(() => estadoInicial056ab(sexo));
    const [refresco, setRefresco] = useState(0);

    const imc = imcDe(estado.medicion.peso, estado.medicion.talla);
    const sexoDistinto = paciente?.gender && paciente.gender !== sexo && (paciente.gender === 'male' || paciente.gender === 'female');

    const handleSubmit = (): Promise<void> =>
      guardar(async () => {
        const ahoraIso = new Date().toISOString();
        const lote = new LoteTransaccional();
        for (const o of observacionesDeToma(tomaDe056ab(estado), subject, encounter, ahoraIso)) lote.crear(o);
        const tanner = observacionTanner(estado, sexo, subject, encounter, ahoraIso);
        if (tanner) lote.crear(tanner);
        await lote.enviar(medplum);
        setRefresco((n) => n + 1);
        // Las cuatro curvas también van al PDF firmado: historial de peso y talla (con lo recién guardado) sobre la referencia de 10 a 19 años.
        // Ya está guardado: si esta búsqueda falla, el PDF sale sin curvas pero no se reporta error (reintentar duplicaría las mediciones).
        const historial = await (medplum.searchResources('Observation', { patient: subject.reference ?? '', code: 'http://loinc.org|29463-7,http://loinc.org|8302-2', 'status:not': 'entered-in-error', _sort: '-date', _count: 200 }) as Promise<Observation[]>).catch(() => undefined);
        const curvas = paciente && historial
          ? datosCurvasAdolescentePdf(paciente, historial, {
              peso: { titulo: t('formularios.f056ab.graficas.peso'), nota: t('formularios.f056ab.graficas.pesoNota') },
              talla: { titulo: t('formularios.f056ab.graficas.talla'), nota: t('formularios.f056ab.graficas.tallaNota') },
              imc: { titulo: t('formularios.f056ab.graficas.imc'), nota: t('formularios.f056ab.graficas.imcNota') },
              velocidad: { titulo: t(`formularios.f056ab.graficas.velocidad${codigo}`), nota: t('formularios.f056ab.graficas.velocidadNotaPdf') },
              ejeX: t('formularios.f056ab.graficas.ejeEdad'),
            })
          : undefined;
        onGuardado(paresDe056ab(estado, sexo), datosEncabezadoMsp, undefined, undefined, undefined, undefined, undefined, undefined, undefined, curvas);
      });

    return (
      <div className="flex flex-col gap-6">
        <h3 className="text-base font-medium text-hce-ink">{t(`formularios.f056ab.titulo${codigo}`)}</h3>

        <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

        {sexoDistinto && (
          <Alert variant="red">
            <AlertDescription>{t(`formularios.f056ab.sexoDistinto${codigo}`)}</AlertDescription>
          </Alert>
        )}

        <Bloque titulo={t('formularios.f056ab.medicionTitulo')}>
          <p className="text-xs text-hce-muted">{t('formularios.f056ab.medicionNota')}</p>
          <CamposSimples prefijoId={`f${codigo.toLowerCase()}-med`} campos={CAMPOS_MEDICION} valores={estado.medicion} columnas={3} onChange={(v) => setEstado((prev) => ({ ...prev, medicion: v }))} />
          <p className="text-sm text-hce-ink">{`${t('formularios.f056ab.imc')}: ${imc !== undefined ? imc : '—'}`}</p>
        </Bloque>

        <Bloque titulo={t('formularios.f056ab.tannerTitulo')}>
          <p className="text-xs text-hce-muted">{t('formularios.f056ab.tannerNota')}</p>
          <CamposSimples prefijoId={`f${codigo.toLowerCase()}-tan`} campos={CAMPOS_TANNER[sexo]} valores={estado.tanner} columnas={2} onChange={(v) => setEstado((prev) => ({ ...prev, tanner: v }))} />
        </Bloque>

        <ErrorGuardado error={error} />

        <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
          {t('formulario.guardar')}
        </Button>

        <Bloque titulo={t('formularios.f056ab.graficasTitulo')}>
          <GraficasAdolescente subject={subject} refresco={refresco} codigo={codigo} />
        </Bloque>
      </div>
    );
  };
}
